import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BaseRiskCalculation,
  CreateRiskCalculation,
  UpdateRiskCalculation,
} from '../dtos/RiskCalculations/index';

class RiskCalculationApi extends BaseApi {
  private readonly basePath = '/risk-calculations';

  /**
   * Get risk calculation by ID
   * GET /api/risk-calculations/{id}
   */
  async getById(id: string): Promise<Response<BaseRiskCalculation | null>> {
    return this.get<BaseRiskCalculation | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all risk calculations
   * GET /api/risk-calculations
   */
  async getList(): Promise<Response<BaseRiskCalculation[]>> {
    return this.get<BaseRiskCalculation[]>(this.basePath);
  }

  /**
   * Get risk calculations by clinical session ID
   * GET /api/risk-calculations/clinical-session/{clinicalSessionId}
   */
  async getByClinicalSessionId(clinicalSessionId: string): Promise<Response<BaseRiskCalculation[]>> {
    return this.get<BaseRiskCalculation[]>(`${this.basePath}/clinical-session/${clinicalSessionId}`);
  }

  /**
   * Get risk calculations by assessment exchange ID
   * GET /api/risk-calculations/assessment-exchange/{assessmentExchangeId}
   */
  async getByAssessmentExchangeId(assessmentExchangeId: string): Promise<Response<BaseRiskCalculation[]>> {
    return this.get<BaseRiskCalculation[]>(`${this.basePath}/assessment-exchange/${assessmentExchangeId}`);
  }

  /**
   * Get paginated list of risk calculations with optional search
   * GET /api/risk-calculations/search?searchTerm=&pageNumber=1&pageSize=10 (when search is provided)
   * GET /api/risk-calculations/paged?pageNumber=1&pageSize=10 (when no search)
   * Searches: CalculationType, AlgorithmVersion, ClinicalSessionId, AssessmentExchangeId
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BaseRiskCalculation>>> {
    if (searchTerm) {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      return this.get<PagedResponse<BaseRiskCalculation>>(
        `${this.basePath}/search?${params.toString()}`
      );
    }
    
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BaseRiskCalculation>>(
      `${this.basePath}/paged?${params.toString()}`
    );
  }

  /**
   * Create a new risk calculation
   * POST /api/risk-calculations
   */
  async create(data: CreateRiskCalculation): Promise<Response<BaseRiskCalculation>> {
    return this.post<BaseRiskCalculation, CreateRiskCalculation>(this.basePath, data);
  }

  /**
   * Update an existing risk calculation
   * PUT /api/risk-calculations/{id}
   */
  async update(id: string, data: Omit<UpdateRiskCalculation, 'id'>): Promise<Response<BaseRiskCalculation>> {
    return this.put<BaseRiskCalculation, UpdateRiskCalculation>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete a risk calculation
   * DELETE /api/risk-calculations/{id}
   */
  async deleteRiskCalculation(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }

  /**
   * Get pending review risk calculations (InReview = true)
   * GET /api/risk-calculations/pending-review?pageNumber=1&pageSize=10
   */
  async getPendingReview(
    pageNumber: number = 1,
    pageSize: number = 10
  ): Promise<Response<PagedResponse<BaseRiskCalculation>>> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BaseRiskCalculation>>(
      `${this.basePath}/pending-review?${params.toString()}`
    );
  }

  /**
   * Update review status of a risk calculation
   * PUT /api/risk-calculations/{id}/review-status
   */
  async updateReviewStatus(id: string, inReview: boolean): Promise<Response<BaseRiskCalculation>> {
    return this.put<BaseRiskCalculation, { inReview: boolean }>(`${this.basePath}/${id}/review-status`, { inReview });
  }

  /**
   * Undo review (set inReview = true)
   * PUT /api/risk-calculations/{id}/undo-review
   */
  async undoReview(id: string): Promise<Response<BaseRiskCalculation>> {
    return this.put<BaseRiskCalculation, {}>(`${this.basePath}/${id}/undo-review`, {});
  }

  /**
   * Update recommendations of a risk calculation
   * PUT /api/risk-calculations/{id}/recommendations
   */
  async updateRecommendations(
    id: string,
    recommendations: { code?: string; text?: string }[]
  ): Promise<Response<BaseRiskCalculation>> {
    return this.put<BaseRiskCalculation, { recommendations: { code?: string; text?: string }[] }>(
      `${this.basePath}/${id}/recommendations`,
      { recommendations }
    );
  }
}

// Export singleton instance
export const riskCalculationApi = new RiskCalculationApi();

export default RiskCalculationApi;
